import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, FileAddOutlined, MailOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import InputNumber from 'antd/es/input-number';
import type { InputNumberProps } from 'antd/es/input-number';
import { Link, useLocation } from 'react-router-dom';
import type { Claim, ClaimsResponse, Metrics, Client, Insurer, ApiResponse, ImportResponse } from '../types/api';
import ClaimImport from './ClaimImport';

const { Title } = Typography;
const { Option } = Select;

const ClaimsManagement: React.FC = (): JSX.Element => {
  const location = useLocation();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClaim, setEditingClaim] = useState<Claim | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('dateReported');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalClaims: 0,
    openClaims: 0,
    closedClaims: 0,
    totalAmount: 0
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);

  const calculateMetrics = useCallback((claimsData: Claim[]) => {
    if (!Array.isArray(claimsData)) {
      console.warn('Invalid claims data for metrics calculation');
      claimsData = [];
    }

    const metrics = {
      totalClaims: claimsData.length,
      openClaims: claimsData.filter(claim => claim?.status === 'OPEN' || claim?.status === 'IN_PROGRESS').length,
      closedClaims: claimsData.filter(claim => claim?.status === 'CLOSED' || claim?.status === 'REJECTED').length,
      totalAmount: claimsData.reduce((sum, claim) => sum + (claim?.amount || 0), 0)
    };
    setMetrics(metrics);
  }, []);

  const fetchClaims = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<ClaimsResponse>('/claims', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchText,
          status: filterStatus,
          type: filterType,
          sortField,
          sortOrder
        }
      });

      const responseData = response.data as ClaimsResponse;
      if (responseData.success) {
        setClaims(responseData.data);
        setPagination(prev => ({
          ...prev,
          total: responseData.total || 0
        }));
        // For server-side pagination, we need to get metrics separately or use the total from server
        if (responseData.metrics) {
          setMetrics(responseData.metrics);
        } else {
          // Fallback: calculate metrics from current page data
          calculateMetrics(responseData.data);
        }
      } else {
        setError(responseData.message || 'Failed to load claims');
        message.error('Failed to load claims');
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
      setError('Failed to load claims');
      message.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, filterStatus, filterType, sortField, sortOrder, calculateMetrics]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: Client[] }>('/clients');
      if (response.data.success) {
        setClients(response.data.data);
      } else {
        message.error('Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      message.error('Failed to fetch clients');
    }
  }, []);

  const fetchInsurers = useCallback(async () => {
    try {
      const response = await api.get<ApiResponse<Insurer[]>>('/insurers');
      if (response.data.success) {
        // Sort insurers alphabetically by name
        const sortedInsurers = response.data.data.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setInsurers(sortedInsurers);
      } else {
        message.error('Failed to fetch insurers');
      }
    } catch (error) {
      console.error('Error fetching insurers:', error);
      message.error('Failed to fetch insurers');
    }
  }, []);

  useEffect(() => {
    fetchClaims();
    fetchClients();
    fetchInsurers();
  }, [fetchClaims, fetchClients, fetchInsurers]);

  // Check if we should open the create modal based on navigation state
  useEffect(() => {
    const state = location.state as { openCreateModal?: boolean };
    if (state?.openCreateModal) {
      setCreateModalVisible(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const validateClaimData = (values: any): string | null => {
    // Required fields validation
    if (!values.claimNumber?.trim()) {
      return 'Claim number is required';
    }
    if (!values.status) {
      return 'Status is required';
    }
    if (!values.type) {
      return 'Type is required';
    }

    // Date validation
    if (values.dateOfLoss && values.dateReported) {
      if (values.dateOfLoss.isAfter(values.dateReported)) {
        return 'Date of Loss cannot be after Date Reported';
      }
      if (values.dateOfLoss.isAfter(dayjs())) {
        return 'Date of Loss cannot be in the future';
      }
    }

    // Amount validation
    if (values.amount) {
      if (values.amount < 0) {
        return 'Amount cannot be negative';
      }
      if (values.amount > 1000000000) {
        return 'Amount seems unreasonably high';
      }
    }

    // Description validation
    if (values.description && values.description.length > 1000) {
      return 'Description is too long (max 1000 characters)';
    }

    // Claim number format validation
    const claimNumberRegex = /^[A-Z0-9-]+$/;
    if (!claimNumberRegex.test(values.claimNumber)) {
      return 'Claim number can only contain uppercase letters, numbers, and hyphens';
    }

    return null;
  };

  const handleSubmit = async (values: any) => {
    try {
      // Validate all fields
      const validationError = validateClaimData(values);
      if (validationError) {
        message.error(validationError);
        return;
      }

      // Format dates to ISO string
      const formattedValues = {
        ...values,
        dateOfLoss: values.dateOfLoss?.toISOString(),
        dateReported: values.dateReported?.toISOString(),
        amount: values.amount || 0,
        description: values.description?.trim() || '',
        status: values.status.toUpperCase(),
        type: values.type.toUpperCase(),
        claimNumber: values.claimNumber.trim().toUpperCase()
      };

      if (editingClaim) {
        // For updates, we need to ensure we're not modifying certain fields
        const updateData = {
          ...formattedValues,
          id: editingClaim.id,
          clientId: values.clientId || editingClaim.clientId,
          insurerId: values.insurerId || editingClaim.insurerId,
          contactId: values.contactId || editingClaim.contactId,
          tenantId: editingClaim.tenantId,
        };

        console.log('Updating claim with data:', updateData);
        const response = await api.put(`/claims/${editingClaim.id}`, updateData);
        
        if (response.data) {
          message.success('Claim updated successfully');
          setIsModalVisible(false);
          form.resetFields();
          setEditingClaim(null);
          fetchClaims();
        } else {
          throw new Error('Failed to update claim');
        }
      } else {
        // For new claims, ensure we have all required fields
        if (!values.clientId || !values.insurerId || !values.contactId) {
          message.error('Please select client, insurer, and contact');
          return;
        }

        console.log('Creating new claim with data:', formattedValues);
        const response = await api.post('/claims', formattedValues);
        
        if (response.data) {
          message.success('Claim created successfully');
          setIsModalVisible(false);
          form.resetFields();
          fetchClaims();
        } else {
          throw new Error('Failed to create claim');
        }
      }
    } catch (error: any) {
      console.error('Error saving claim:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save claim';
      message.error(errorMessage);
    }
  };

  const handleEdit = (claim: Claim) => {
    try {
      if (!claim) {
        throw new Error('Invalid claim data');
      }

      setEditingClaim(claim);
      const formattedClaim = {
        ...claim,
        dateOfLoss: claim.dateOfLoss ? dayjs(claim.dateOfLoss) : null,
        dateReported: claim.dateReported ? dayjs(claim.dateReported) : null,
        clientId: claim.clientId,
        insurerId: claim.insurerId,
        contactId: claim.contactId,
        status: claim.status.toLowerCase(),
        type: claim.type.toLowerCase(),
        claimNumber: claim.claimNumber.trim()
      };
      form.setFieldsValue(formattedClaim);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error editing claim:', error);
      message.error('Failed to load claim data');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      if (!id) {
        throw new Error('Invalid claim ID');
      }

      Modal.confirm({
        title: 'Are you sure you want to delete this claim?',
        content: 'This action cannot be undone.',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          const response = await api.delete(`/claims/${id}`);
          if (response.data) {
            message.success('Claim deleted successfully');
            fetchClaims();
          } else {
            throw new Error('Failed to delete claim');
          }
        }
      });
    } catch (error: any) {
      console.error('Error deleting claim:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete claim';
      message.error(errorMessage);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      if (!id || !newStatus) {
        throw new Error('Invalid status change request');
      }

      // Validate status value
      const validStatuses = ['open', 'in_progress', 'closed', 'rejected'];
      if (!validStatuses.includes(newStatus.toLowerCase())) {
        throw new Error('Invalid status value');
      }

      const response = await api.put(`/claims/${id}/status`, { status: newStatus });
      if (response.data) {
        message.success('Status updated successfully');
        fetchClaims();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update status';
      message.error(errorMessage);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;

    try {
      const response = await api.post('/claims/bulk-delete', {
        ids: selectedRowKeys
      });

      if (response.data.success) {
        message.success('Selected claims deleted successfully');
        setSelectedRowKeys([]);
        fetchClaims();
      } else {
        message.error(response.data.message || 'Failed to delete claims');
      }
    } catch (error) {
      console.error('Error deleting claims:', error);
      message.error('Failed to delete claims');
    }
  };

  const handleBulkEmail = async () => {
    if (!selectedRowKeys.length) return;

    try {
      const response = await api.post('/claims/bulk-email', {
        ids: selectedRowKeys
      });

      if (response.data.success) {
        message.success('Email sent successfully');
      } else {
        message.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      message.error('Failed to send email');
    }
  };

  const handleImport = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post<ImportResponse>('/claims/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        message.success(`Successfully imported ${response.data.imported} claims`);
        if (response.data.errors > 0) {
          message.warning(`${response.data.errors} claims failed to import`);
        }
        fetchClaims();
      } else {
        throw new Error(response.data.error || 'Failed to import claims');
      }
    } catch (err: any) {
      console.error('Import error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to import claims';
      const errorDetails = err.response?.data?.details || '';
      message.error(
        <div>
          <p>{errorMessage}</p>
          {errorDetails && <pre style={{ fontSize: '12px', color: '#666' }}>{errorDetails}</pre>}
        </div>
      );
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/claims/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `claims_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Claims exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      message.error('Failed to export claims');
    }
  };

  const handleCreate = () => {
    setEditingClaim(null);
    form.resetFields();
    setCreateModalVisible(true);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilter = (status: string | null, type: string | null) => {
    setFilterStatus(status);
    setFilterType(type);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Claim> | SorterResult<Claim>[]
  ): void => {
    setPagination(pagination);
    if (Array.isArray(sorter)) {
      setSortField(sorter[0]?.field as string);
      setSortOrder(sorter[0]?.order as 'ascend' | 'descend');
    } else {
      setSortField(sorter.field as string);
      setSortOrder(sorter.order as 'ascend' | 'descend');
    }
  };

  const columns: ColumnsType<Claim> = [
    {
      title: 'File Number',
      dataIndex: 'file_number',
      key: 'file_number',
      sorter: true,
      render: (text: string, record: Claim) => (
        <Link
          to={`/claims/${record.file_number}`}
          state={{ fileNumber: record.file_number, insuredName: record.insuredName }}
          className="text-blue-600 hover:text-blue-800"
        >
          {text}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Open', value: 'OPEN' },
        { text: 'In Progress', value: 'IN_PROGRESS' },
        { text: 'Closed', value: 'CLOSED' },
        { text: 'Rejected', value: 'REJECTED' },
      ],
      render: (status: Claim['status']) => {
        const statusColors = {
          OPEN: 'blue',
          IN_PROGRESS: 'orange',
          CLOSED: 'green',
          REJECTED: 'red',
        };
        return (
          <Tag color={statusColors[status]}>
            {status.replace('_', ' ')}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Property', value: 'PROPERTY' },
        { text: 'Liability', value: 'LIABILITY' },
        { text: 'Health', value: 'HEALTH' },
        { text: 'Auto', value: 'AUTO' },
      ],
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      render: (amount: number) => (
        <span className="font-medium">
          ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Date Reported',
      dataIndex: 'dateReported',
      key: 'dateReported',
      sorter: true,
      render: (date: string) => date ? dayjs(date).format('MMM D, YYYY') : '-',
    },
    {
      title: 'Insured',
      dataIndex: 'insuredName',
      key: 'insuredName',
      render: (text: string, record: Claim) => {
        if (!record.client) {
          return <span>{text || '-'}</span>;
        }
        const firstName = record.client.firstName || '';
        const lastName = record.client.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return <span>{fullName || text || '-'}</span>;
      },
    },
    {
      title: 'Insurer',
      dataIndex: 'insurerName',
      key: 'insurerName',
      render: (text: string, record: Claim) => (
        <span>{record.insurer?.name ?? text ?? '-'}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: Claim) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleCreateClaim = async (values: any) => {
    try {
      if (clients.length === 0) {
        message.error('Please create a client first');
        return;
      }
      if (insurers.length === 0) {
        message.error('Please create an insurer first');
        return;
      }

      const response = await api.post('/claims', {
        ...values,
        file_number: values.file_number || `FILE-${Date.now()}`,
        status: values.status || 'OPEN'
      });
      message.success('Claim created successfully');
      fetchClaims();
      setCreateModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error creating claim:', error);
      message.error('Failed to create claim');
    }
  };

  const handleCreateTestData = async () => {
    try {
      const response = await api.post('/claims/test-data');
      message.success('Test data created successfully');
      fetchClaims();
      fetchClients();
      fetchInsurers();
    } catch (error) {
      console.error('Error creating test data:', error);
      message.error('Failed to create test data');
    }
  };

  return (
    <div className="p-6">

      {selectedRowKeys.length > 0 && (
        <div className="mb-4">
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
            <Button
              icon={<MailOutlined />}
              onClick={handleBulkEmail}
            >
              Email Selected
            </Button>
          </Space>
        </div>
      )}

      <Row gutter={[16, 16]} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Claims"
              value={metrics.totalClaims}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Open Claims"
              value={metrics.openClaims}
              loading={loading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Closed Claims"
              value={metrics.closedClaims}
              loading={loading}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={metrics.totalAmount}
              loading={loading}
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div className="flex justify-between items-center mb-4">
          <Space>
            <Input
              placeholder="Search claims..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => handleSearch(searchText)}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => handleFilter(value, filterType)}
            >
              <Option value="OPEN">Open</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="CLOSED">Closed</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>
            <Select
              placeholder="Type"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => handleFilter(filterStatus, value)}
            >
              <Option value="PROPERTY">Property</Option>
              <Option value="LIABILITY">Liability</Option>
              <Option value="HEALTH">Health</Option>
              <Option value="AUTO">Auto</Option>
            </Select>
          </Space>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Claim
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />} 
              onClick={() => setShowImportModal(true)}
            >
              Import Claims
            </Button>
            <Button
              type="default"
              icon={<FileExcelOutlined />}
              onClick={handleExport}
            >
              Export Claims
            </Button>
          </Space>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <Table<Claim>
          columns={columns}
          dataSource={claims}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title="Create New Claim"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateClaim}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="file_number"
                label="File Number"
                rules={[{ required: true, message: 'Please enter a file number' }]}
              >
                <Input placeholder="Enter file number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="claimNumber"
                label="Claim Number"
                rules={[{ required: true, message: 'Please enter a claim number' }]}
              >
                <Input placeholder="Enter claim number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="policyNumber"
                label="Policy Number"
                rules={[{ required: true, message: 'Please enter a policy number' }]}
              >
                <Input placeholder="Enter policy number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select placeholder="Select status">
                  <Select.Option value="OPEN">Open</Select.Option>
                  <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                  <Select.Option value="CLOSED">Closed</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter claim description" />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter an amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value: number | undefined, info: { userTyping: boolean; input: string }) => 
                value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
              }
              parser={(displayValue: string | undefined) => 
                displayValue ? Number(displayValue.replace(/\$\s?|(,*)/g, '')) : 0
              }
              placeholder="Enter claim amount"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="clientId"
                label="Client"
                rules={[{ required: true, message: 'Please select a client' }]}
              >
                <Select placeholder="Select client">
                  {clients.map(client => (
                    <Select.Option key={client.id} value={client.id}>
                      {client.type === 'COMPANY' ? client.companyName : `${client.firstName} ${client.lastName}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="insurerId"
                label="Insurer"
                rules={[{ required: true, message: 'Please select an insurer' }]}
              >
                <Select placeholder="Select insurer">
                  {insurers.map(insurer => (
                    <Select.Option key={insurer.id} value={insurer.id}>
                      {insurer.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Create Claim
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <ClaimImport
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          fetchClaims();
        }}
      />
    </div>
  );
};

export default ClaimsManagement; 